// ─── Template: progress-bar-v1 ────────────────────────────────────────────
// Barra de progreso con etiqueta y porcentaje animado. Usa `spring()` de
// Remotion para una entrada natural — es uno de los patrones estándar
// que recomienda la documentación de Remotion para animaciones numéricas.

import React from 'react';
import { AbsoluteFill, interpolate, spring, useCurrentFrame, useVideoConfig } from 'remotion';
import type { AnimationTemplate } from '../types';

export type AlignPreset = 'left' | 'center' | 'right';

export type ProgressBarProps = {
  label: string;
  /** Valor final, entre 0 y 100. */
  percent: number;
  barColor: string;
  trackColor: string;
  /** Alto de la barra en % de la altura de la composición. */
  barHeight: number;
  /** Bordes redondeados (sí/no). */
  rounded: boolean;
  /** Mostrar el número al lado (ej. "72%"). */
  showValue: boolean;
  labelFont: string;
  labelSize: number;
  labelColor: string;
  labelBold: boolean;
  labelItalic: boolean;
  labelAlign: AlignPreset;
  background: string;
};

const Component: React.FC<ProgressBarProps> = ({
  label, percent, barColor, trackColor, barHeight, rounded, showValue,
  labelFont, labelSize, labelColor, labelBold, labelItalic, labelAlign,
  background,
}) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();
  // Lado más corto: usamos esto como referencia para todo lo que tenga
  // que verse "igual de grande" en horizontal y vertical.
  const refDim = Math.min(width, height);
  const labelPx = Math.max(8, (labelSize / 100) * refDim);

  // Spring para animar el llenado de la barra. Es la forma canónica que
  // recomienda Remotion para animar valores numéricos: dampening 200 da
  // una sensación rígida pero natural.
  const progress = spring({
    frame,
    fps,
    config: { damping: 20, mass: 0.8 },
  });
  // `progress` va de 0 a 1. Lo mapeamos al porcentaje objetivo.
  const animatedPercent = interpolate(progress, [0, 1], [0, Math.max(0, Math.min(100, percent))]);

  // Fade-in del contenedor entero.
  const containerOpacity = interpolate(frame, [0, 10], [0, 1], { extrapolateRight: 'clamp' });

  const fontFamily = labelFont
    ? `${labelFont}, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`
    : 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

  const justify = labelAlign === 'center' ? 'center' : labelAlign === 'right' ? 'flex-end' : 'flex-start';
  // La barra ocupa el 72% del ancho disponible (padding a ambos lados).
  const barWidth = width * 0.72;
  const barH = Math.max(4, (barHeight / 100) * refDim);

  return (
    <AbsoluteFill style={{
      background, padding: '0 6%',
      display: 'flex', flexDirection: 'column',
      justifyContent: 'center', alignItems: justify,
      gap: 16, opacity: containerOpacity,
    }}>
      {/* Encabezado: etiqueta + porcentaje animado */}
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 16,
        width: barWidth, justifyContent: 'space-between',
      }}>
        <div style={{
          fontFamily,
          fontSize: labelPx,
          fontWeight: labelBold ? 700 : 500,
          fontStyle: labelItalic ? 'italic' : 'normal',
          color: labelColor,
        }}>
          {label}
        </div>
        {showValue && (
          <div style={{
            fontFamily,
            fontSize: labelPx,
            fontWeight: 700,
            color: labelColor,
            fontVariantNumeric: 'tabular-nums',
          }}>
            {Math.round(animatedPercent)}%
          </div>
        )}
      </div>

      {/* Track + relleno */}
      <div style={{
        width: barWidth, height: barH,
        background: trackColor,
        borderRadius: rounded ? barH / 2 : 4,
        overflow: 'hidden',
        position: 'relative',
      }}>
        <div style={{
          width: `${animatedPercent}%`, height: '100%',
          background: barColor,
          borderRadius: rounded ? barH / 2 : 4,
          transition: 'none',
        }} />
      </div>
    </AbsoluteFill>
  );
};

const ALIGN_OPTIONS = [
  { value: 'left',   label: 'Izquierda' },
  { value: 'center', label: 'Centro' },
  { value: 'right',  label: 'Derecha' },
];

export const progressBarTemplate: AnimationTemplate<ProgressBarProps> = {
  id: 'progress-bar-v1',
  label: 'Barra de progreso',
  description: 'Barra animada que se llena desde cero hasta el porcentaje elegido.',
  category: 'text',
  defaultDurationSec: 3,
  defaultProps: {
    label: 'Carga del proyecto',
    percent: 72,
    barColor: '#6366F1',
    trackColor: '#1F2937',
    barHeight: 3,
    rounded: true,
    showValue: true,
    labelFont: 'Inter',
    labelSize: 2.6,
    labelColor: '#FFFFFF',
    labelBold: true,
    labelItalic: false,
    labelAlign: 'center',
    background: '#0B0F1A',
  },
  propsSchema: {
    label:   { type: 'text', label: 'Etiqueta', placeholder: 'Qué se está midiendo', agentExposed: true,
               styling: { font: 'labelFont', size: 'labelSize', color: 'labelColor',
                 bold: 'labelBold', italic: 'labelItalic', align: 'labelAlign' } },
    percent: { type: 'number', label: 'Porcentaje final', min: 0, max: 100, step: 1, unit: '%' },
    barColor:   { type: 'color',   label: 'Color de la barra' },
    trackColor: { type: 'color',   label: 'Color del fondo de la barra' },
    barHeight:  { type: 'number',  label: 'Grosor', min: 1, max: 10, step: 0.5, decimals: 1, unit: '%' },
    rounded:    { type: 'boolean', label: 'Bordes redondeados' },
    showValue:  { type: 'boolean', label: 'Mostrar el número' },
    labelFont:   { type: 'font',    label: 'Tipografía' },
    labelSize:   { type: 'number',  label: 'Tamaño de la etiqueta', min: 1, max: 10, step: 0.1, decimals: 1, unit: '%' },
    labelColor:  { type: 'color',   label: 'Color de la etiqueta' },
    labelBold:   { type: 'boolean', label: 'Negrita' },
    labelItalic: { type: 'boolean', label: 'Cursiva' },
    labelAlign:  { type: 'select',  label: 'Alineación', options: ALIGN_OPTIONS },
    background:  { type: 'color',   label: 'Fondo', allowTransparent: true },
  },
  Component,
};
